import nullifyEmptyString from '@watershed/shared-universal/utils/nullifyEmptyString';
import {
  TextFieldNonFormik,
  TextFieldMultilineNonFormik,
  TextFieldMultilineProps,
} from '@watershed/ui-core/components/Form/TextField';
import { SyntheticEvent } from 'react';
/* need to import react-hook-form for typescript to infer the type of ZodForm below */
import 'react-hook-form';
import MarkdownTextFieldWithPreview from '@watershed/ui-core/components/MarkdownTextFieldWithPreview';
import Field from '@watershed/ui-core/components/Form/Field';

import React from 'react';
import { CommonControllerProps } from './fieldUtils';
import {
  CommonZodFieldProps,
  getInputFieldProps,
  getCommonFieldProps,
  useFieldPropsFromTsController,
} from './fieldUtils';
import {
  AbstractSelectProps,
  ZodAbstractSelectField,
  ZodSelectFieldEditorType,
} from './ZodSelectFields';
import Button from '@watershed/ui-core/components/Button';
import { Box, Stack } from '@mui/material';
import IconButton from '@watershed/ui-core/components/IconButton';
import CircleCloseIcon from '@watershed/icons/components/CircleClose';
import AddIcon from '@watershed/icons/components/Add';
import { ZodFormFragmentField } from './ZodForm';
import * as z from 'zod';

type ZodAbstractTextFieldProps<T> = {
  isMarkdown?: boolean;
  multiline?: boolean | Pick<TextFieldMultilineProps, 'rows'>;
  onSelect?: (v: SyntheticEvent) => void;
  onBlur?: (v: SyntheticEvent) => void;
  onKeyDown?(
    evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void;
  onKeyUp?(
    evt: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void;
  nullifyEmptyString?: boolean;
  maxLength?: number;
} & CommonZodFieldProps &
  CommonControllerProps<T>;

function ZodAbstractTextField({
  nullifyEmptyString: shouldNullifyEmptyString = true,
  ...props
}: ZodAbstractTextFieldProps<string>) {
  const {
    onChange: _onChange,
    value: _value,
    ...tsProps
  } = useFieldPropsFromTsController<string | null>({
    onChangeTransform: props.onChangeTransform,
  });
  const value = _value ?? '';
  const onChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) =>
    _onChange(
      shouldNullifyEmptyString
        ? nullifyEmptyString(e.target.value)
        : e.target.value
    );
  if (props.isMarkdown) {
    return (
      <Field
        inputId={tsProps.id}
        label={tsProps.label}
        {...getCommonFieldProps(props)}
      >
        <MarkdownTextFieldWithPreview
          {...tsProps}
          {...getInputFieldProps(props)}
          value={value}
          onChange={onChange}
          previewSx={{
            // on the Dashboard, we also remove this margin. See CP-990.
            '& ul': {
              marginTop: 0,
              marginBottom: 0,
            },
          }}
        />
      </Field>
    );
  }
  const { multiline = { rows: 1 } } = props;
  return multiline ? (
    <TextFieldMultilineNonFormik
      {...tsProps}
      {...getCommonFieldProps(props)}
      {...getInputFieldProps(props)}
      {...(typeof multiline === 'object' ? multiline : undefined)}
      value={value}
      onChange={onChange}
      data-test={`ZodField-${props.name}`}
    />
  ) : (
    <TextFieldNonFormik
      {...tsProps}
      {...getCommonFieldProps(props)}
      {...getInputFieldProps(props)}
      value={value}
      onChange={onChange}
      data-test={`ZodField-${props.name}`}
    />
  );
}

export function ZodTextField({
  ...props
}:
  | ZodAbstractTextFieldProps<string | null>
  | AbstractSelectProps<string | null>) {
  if ('listOptions' in props) {
    return <ZodAbstractSelectField<string | null> {...props} />;
  }
  return <ZodAbstractTextField {...props} />;
}

function ZodMultiTextInputField({
  label,
  ...props
}: ZodAbstractTextFieldProps<Array<string | null | undefined> | null>) {
  const {
    onChange,
    value: _value,
    ...tsProps
  } = useFieldPropsFromTsController<Array<string | null | undefined> | null>({
    onChangeTransform: props.onChangeTransform,
  });

  const value = _value ?? [];

  const append = (val: string | null) => {
    onChange([...value, val]);
  };

  const remove = (index: number) => {
    onChange([...value.slice(0, index), ...value.slice(index + 1)]);
  };

  return (
    <Field
      inputId={tsProps.id}
      label={tsProps.label}
      {...getCommonFieldProps(props)}
    >
      <Stack gap={1}>
        {value.map((f, index) => {
          return (
            <Stack direction="row" key={index} gap={1}>
              <ZodFormFragmentField
                props={{ ...props, label: '' } as any}
                schema={z.string()}
                schemaKey={index}
              />
              <IconButton
                title="Remove this value"
                onClick={() => remove(index)}
              >
                <CircleCloseIcon />
              </IconButton>
            </Stack>
          );
        })}
        <Box>
          <Button
            onClick={() => {
              append('');
            }}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Box>
      </Stack>
    </Field>
  );
}

/**
 * Multi select string field.
 */
export function ZodTextArrayField({
  ...restProps
}:
  | ZodAbstractTextFieldProps<Array<string | null | undefined> | null>
  | AbstractSelectProps<string | null>) {
  if (!('listOptions' in restProps)) {
    return <ZodMultiTextInputField {...restProps} />;
  }
  return (
    <ZodAbstractSelectField<string | null>
      editorType={ZodSelectFieldEditorType.SelectAutocomplete}
      {...restProps}
      multiple={restProps.multiple ?? true}
    />
  );
}
